import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Button, DatePicker, Modal, Radio, Space, Spin, Typography} from "antd";
import {DownloadOutlined} from "@ant-design/icons";
import ReactECharts from 'echarts-for-react';
import {CSVLink} from "react-csv";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {internalApi} from "../../../common/axios";
import {handleResponse} from "../../../shared/utils/net";
import {DarkModeContext} from "../../../shared/contexts/darkModeContext";

const {Text} = Typography;

// 서버 버킷 라벨(day: YYYY-MM-DD, week: 월요일 YYYY-MM-DD, month: YYYY-MM)과 맞춘다.
const UNIT = {
    day: {label: '일별', format: 'YYYY-MM-DD', start: (d) => d},
    week: {label: '주간별', format: 'YYYY-MM-DD', start: (d) => d.subtract((d.day() + 6) % 7, 'day')},
    month: {label: '월간별', format: 'YYYY-MM', start: (d) => d.startOf('month')},
};

// 서버 응답 필드. 그래프 시리즈, 합계, CSV 컬럼에 공통으로 쓴다.
const USER_TYPES = [
    {key: 'newUser', label: '신규 가입자'},
    {key: 'returnUser', label: '복귀 가입자'},
    {key: 'normalUser', label: '일반 사용자'},
];
const CSV_HEADERS = [{label: '기간', key: 'date'}, {label: '전체', key: 'count'}, ...USER_TYPES.map(({key, label}) => ({key, label}))];
// 쿠폰 공개일. 이 시각 이후 가입한 유저를 신규로 본다.
const DEFAULT_NEW_SINCE = dayjs('2026-09-30 10:00:00');

// 사용 내역이 없는 구간도 0 으로 채워 그래프에 빈 칸이 생기지 않게 한다.
const fillBuckets = (rows, unit, [from, to]) => {
    const {format, start} = UNIT[unit];
    const byDate = new Map(rows.map(r => [r.date, r]));
    const result = [];
    for (let d = start(from.startOf('day')); !d.isAfter(to, 'day'); d = d.add(1, unit)) {
        const date = d.format(format);
        const row = byDate.get(date) || {};
        result.push(Object.fromEntries([['date', date], ...CSV_HEADERS.slice(1).map(({key}) => [key, Number(row[key]) || 0])]));
    }
    return result;
};

const SanrioCouponStats = () => {
    const {darkMode} = useContext(DarkModeContext);
    const [open, setOpen] = useState(false);
    // 모달이 body 에 붙기 전에 차트가 마운트되면 크기 0 으로 그려지므로 열림이 끝난 뒤에 그린다.
    const [opened, setOpened] = useState(false);
    const [unit, setUnit] = useState('day');
    const [range, setRange] = useState([dayjs().subtract(6, 'day'), dayjs()]);
    const [newSince, setNewSince] = useState(DEFAULT_NEW_SINCE);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !range?.[0] || !range?.[1] || !newSince) return;
        const [from, to] = range;
        setLoading(true);
        internalApi.get('coupon/sanrio_stats', {
            params: {
                unit,
                start: from.format('YYYY-MM-DD'),
                end: to.format('YYYY-MM-DD'),
                newSince: newSince.format('YYYY-MM-DD HH:mm:ss'),
            }
        }).then(resp => handleResponse(resp,
            ({rows}) => setData(fillBuckets(rows, unit, range)),
            (msg) => toast.error(`${msg}`)
        )).catch(() => toast.error('통계 조회 실패'))
            .finally(() => setLoading(false));
    }, [open, unit, range, newSince]);

    const totals = useMemo(() => Object.fromEntries(CSV_HEADERS.slice(1).map(({key}) =>
        [key, data.reduce((sum, r) => sum + r[key], 0)])), [data]);

    const option = useMemo(() => ({
        backgroundColor: 'transparent',
        tooltip: {trigger: 'axis'},
        legend: {top: 0},
        grid: {left: 50, right: 20, top: 40, bottom: 60},
        xAxis: {type: 'category', data: data.map(r => r.date)},
        yAxis: {type: 'value', minInterval: 1},
        dataZoom: [{type: 'inside'}, {type: 'slider'}],
        series: USER_TYPES.map(({key, label}) => ({
            name: label,
            type: 'bar',
            stack: 'total',
            data: data.map(r => r[key]),
            label: {show: data.length <= 31, formatter: ({value}) => value || ''},
        })),
    }), [data]);

    return (
        <>
            <Button style={{margin: 10}} onClick={() => setOpen(true)}>산리오 플러스 쿠폰 통계</Button>
            <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={1000} centered destroyOnClose
                   afterOpenChange={setOpened}
                   title="산리오 플러스 쿠폰 사용 통계 (Live)">
                <Space wrap style={{marginBottom: 16}}>
                    <Radio.Group value={unit} onChange={(e) => setUnit(e.target.value)} optionType="button"
                                 options={Object.entries(UNIT).map(([value, {label}]) => ({value, label}))}/>
                    <DatePicker.RangePicker value={range} onChange={setRange} allowClear={false}
                                            disabledDate={(d) => d.isAfter(dayjs(), 'day')}/>
                    <Space size={4}>
                        <Text>신규 기준</Text>
                        <DatePicker showTime value={newSince} onChange={setNewSince} allowClear={false}/>
                    </Space>
                    <CSVLink data={data} headers={CSV_HEADERS}
                             filename={`sanrio_plus_coupon_${unit}_${range[0].format('YYYYMMDD')}_${range[1].format('YYYYMMDD')}.csv`}>
                        <Button icon={<DownloadOutlined/>} disabled={loading || data.length === 0}>CSV 다운로드</Button>
                    </CSVLink>
                </Space>
                <div style={{marginBottom: 8}}>
                    <Space size="large" wrap>
                        <Text strong>전체: {totals.count?.toLocaleString()}명</Text>
                        {USER_TYPES.map(({key, label}) => <Text key={key}>{label}: {totals[key]?.toLocaleString()}명</Text>)}
                    </Space>
                </div>
                <Spin spinning={loading}>
                    {opened ? (
                        <ReactECharts option={option} theme={darkMode ? 'dark' : undefined} notMerge
                                      style={{height: 420}}/>
                    ) : <div style={{height: 420}}/>}
                </Spin>
            </Modal>
        </>
    );
};

export default SanrioCouponStats;
